// Mock Authentication Hook for Development
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  getCurrentMockUser,
  setCurrentMockUser,
  clearCurrentMockUser,
  type MockUser,
} from '../lib/mock-users';

interface MockAuthContextType {
  user: MockUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (userKey: string) => Promise<void>;
  logout: () => Promise<void>;
  switchRole: (role: 'admin' | 'creator' | 'user') => Promise<void>;
  updateUser: (updates: Partial<MockUser>) => void;
}

const MockAuthContext = createContext<MockAuthContextType | undefined>(undefined);

export function MockAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<MockUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Initialize from sessionStorage
    const storedUser = getCurrentMockUser();
    setUser(storedUser);
    setIsLoading(false);
  }, []);

  const login = async (userKey: string): Promise<void> => {
    setIsLoading(true);
    try {
      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 300));

      const mockUser = setCurrentMockUser(userKey);
      setUser(mockUser);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    setIsLoading(true);
    try {
      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 200));

      clearCurrentMockUser();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const switchRole = async (role: 'admin' | 'creator' | 'user'): Promise<void> => {
    if (!user) return;

    setIsLoading(true);
    try {
      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 300));

      const updatedUser = { ...user, role };
      sessionStorage.setItem('mockUser', JSON.stringify(updatedUser));
      setUser(updatedUser);
    } finally {
      setIsLoading(false);
    }
  };

  const updateUser = (updates: Partial<MockUser>): void => {
    if (!user) return;

    const updatedUser = { ...user, ...updates };
    sessionStorage.setItem('mockUser', JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  const value: MockAuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    switchRole,
    updateUser,
  };

  return <MockAuthContext.Provider value={value}>{children}</MockAuthContext.Provider>;
}

export function useMockAuth(): MockAuthContextType {
  const context = useContext(MockAuthContext);
  if (context === undefined) {
    throw new Error('useMockAuth must be used within a MockAuthProvider');
  }
  return context;
}

// Hook for checking permissions
export function usePermissions() {
  const { user } = useMockAuth();

  return {
    isAdmin: user?.role === 'admin',
    isCreator: user?.role === 'creator' || user?.role === 'admin',
    isUser: !!user,
    canManageUsers: user?.role === 'admin',
    canManageProducts: user?.role === 'admin' || user?.role === 'creator',
    canPurchase: user?.status === 'active',
  };
}
